import {
  ANLIEGEN,
  BESCHWERDEDAUER,
  evaluateQuestionnaire,
  RED_FLAGS,
  SCHWEREGRAD,
  type CaseForPharmacy,
  type QuestionnaireAnswers,
} from "@omony/domain";
import { Banner, Button, Checkbox, Segmented, Select } from "@omony/ui";
import { ClipboardCheck, UserCheck } from "lucide-react";
import { useState } from "react";
import { ErrorBanner } from "../../shared/auth/screens.js";
import { useCaseAction, useFeatures } from "../data.js";
import { PatientLanguage } from "../PatientLanguage.js";
import { Spacer, StepFrame, useT } from "./common.js";
import styles from "./flow.module.css";
import { SmedPanel } from "./SmedPanel.js";

/** Schritt Ersteinschätzung (A12–A15): bekannter Patient, SmED oder eigener Bogen */
export function TriageStep({ c }: { c: CaseForPharmacy }) {
  const features = useFeatures();
  const [useBogen, setUseBogen] = useState(false);
  if (c.knownPatient) return <KnownPatient c={c} />;
  if (!features.data) return null;
  if (features.data.smed.enabled && !useBogen)
    return <SmedPanel c={c} onFallback={() => setUseBogen(true)} />;
  return <Questionnaire c={c} />;
}

function KnownPatient({ c }: { c: CaseForPharmacy }) {
  const t = useT();
  const action = useCaseAction<{ version: number }>(c.id, "triage/known-patient");
  return (
    <StepFrame
      eyebrow={t("console.step.ersteinschaetzung")}
      title={t("console.known.title")}
      lead={t("console.known.lead")}
      actions={
        <>
          <Spacer />
          <Button
            size="lg"
            icon={<UserCheck />}
            onPress={() => action.mutate({ version: c.version })}
            isDisabled={action.isPending}
          >
            {t("console.known.submit")}
          </Button>
        </>
      }
    >
      <ErrorBanner error={action.error} />
    </StepFrame>
  );
}

type Answers = Omit<QuestionnaireAnswers, "anliegen" | "dauer" | "schwere"> & {
  anliegen: QuestionnaireAnswers["anliegen"] | null;
  dauer: QuestionnaireAnswers["dauer"] | null;
  schwere: QuestionnaireAnswers["schwere"] | null;
};

function Questionnaire({ c }: { c: CaseForPharmacy }) {
  const t = useT();
  const [a, setA] = useState<Answers>({
    redFlags: [],
    minderjaehrig: c.label.age !== null && c.label.age < 18,
    schwangerOderStillend: false,
    anliegen: null,
    dauer: null,
    schwere: null,
    selbstauskunft: false,
  });
  const [problem, setProblem] = useState<"incomplete" | "selbstauskunft_required" | null>(null);
  const action = useCaseAction<QuestionnaireAnswers & { version: number }>(c.id, "triage/questionnaire");

  const submit = () => {
    if (!a.anliegen || !a.dauer || !a.schwere) {
      // Warnzeichen führen auch ohne die übrigen Angaben sofort zum Notfall-Hinweis
      if (!a.redFlags.length) return setProblem("incomplete");
    }
    const answers: QuestionnaireAnswers = {
      ...a,
      anliegen: a.anliegen ?? "sonstiges",
      dauer: a.dauer ?? "unter_24h",
      schwere: a.schwere ?? "stark",
    };
    const r = evaluateQuestionnaire(answers);
    if (!r.ok) return setProblem(r.error);
    setProblem(null);
    action.mutate({ ...answers, version: c.version });
  };

  return (
    <StepFrame
      eyebrow={t("console.step.ersteinschaetzung")}
      title={t("console.bogen.title")}
      lead={t("console.bogen.lead")}
      actions={
        <>
          <Spacer />
          <Button size="lg" icon={<ClipboardCheck />} onPress={submit} isDisabled={action.isPending}>
            {t("console.bogen.submit")}
          </Button>
        </>
      }
    >
      <Banner tone="info">{t("console.bogen.draftNote")}</Banner>
      <PatientLanguage locale={c.patientLanguage}>
        <QuestionnaireFields
          a={a}
          onChange={(p) => {
            setA((x) => ({ ...x, ...p }));
            setProblem(null);
          }}
        />
      </PatientLanguage>
      {problem ? (
        <Banner tone="danger">
          {t(problem === "incomplete" ? "console.bogen.incomplete" : "error.selbstauskunft_required")}
        </Banner>
      ) : null}
      <ErrorBanner error={action.error} />
    </StepFrame>
  );
}

/** Fragen in Patientensprache (A13) */
function QuestionnaireFields({ a, onChange }: { a: Answers; onChange: (p: Partial<Answers>) => void }) {
  const t = useT();
  const yesNo = [
    { id: "nein", label: t("common.no") },
    { id: "ja", label: t("common.yes") },
  ];
  const toggleFlag = (f: (typeof RED_FLAGS)[number], on: boolean) =>
    onChange({ redFlags: on ? [...a.redFlags, f] : a.redFlags.filter((x) => x !== f) });
  return (
    <>
      <fieldset className={styles.section}>
        <legend className={styles.sectionTitle}>{t("patient.bogen.redFlags")}</legend>
        {RED_FLAGS.map((f) => (
          <Checkbox key={f} card isSelected={a.redFlags.includes(f)} onChange={(v) => toggleFlag(f, v)}>
            {t(`patient.bogen.flag.${f}`)}
          </Checkbox>
        ))}
      </fieldset>
      <div className={styles.grid2}>
        <div className={styles.section}>
          <span className={styles.sectionTitle}>{t("patient.bogen.minor")}</span>
          <Segmented
            label={t("patient.bogen.minor")}
            items={yesNo}
            value={a.minderjaehrig ? "ja" : "nein"}
            onChange={(v) => onChange({ minderjaehrig: v === "ja" })}
          />
        </div>
        <div className={styles.section}>
          <span className={styles.sectionTitle}>{t("patient.bogen.pregnant")}</span>
          <Segmented
            label={t("patient.bogen.pregnant")}
            items={yesNo}
            value={a.schwangerOderStillend ? "ja" : "nein"}
            onChange={(v) => onChange({ schwangerOderStillend: v === "ja" })}
          />
        </div>
      </div>
      <div className={styles.grid2}>
        <Select
          label={t("patient.bogen.anliegen")}
          value={a.anliegen}
          placeholder={t("patient.bogen.choose")}
          onChange={(v) => onChange({ anliegen: v as Answers["anliegen"] })}
          options={ANLIEGEN.map((x) => ({ id: x, label: t(`patient.bogen.anliegen.${x}`) }))}
        />
        <Select
          label={t("patient.bogen.dauer")}
          value={a.dauer}
          placeholder={t("patient.bogen.choose")}
          onChange={(v) => onChange({ dauer: v as Answers["dauer"] })}
          options={BESCHWERDEDAUER.map((x) => ({ id: x, label: t(`patient.bogen.dauer.${x}`) }))}
        />
        <Select
          label={t("patient.bogen.schwere")}
          value={a.schwere}
          placeholder={t("patient.bogen.choose")}
          onChange={(v) => onChange({ schwere: v as Answers["schwere"] })}
          options={SCHWEREGRAD.map((x) => ({ id: x, label: t(`patient.bogen.schwere.${x}`) }))}
        />
      </div>
      <Checkbox card isSelected={a.selbstauskunft} onChange={(v) => onChange({ selbstauskunft: v })}>
        {t("patient.bogen.selbstauskunft")}
      </Checkbox>
    </>
  );
}
